
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Upload, FileImage, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface KYCAccessGuardProps {
  children: React.ReactNode;
  requireDocuments?: boolean;
}

const KYCAccessGuard = ({ children, requireDocuments = true }: KYCAccessGuardProps) => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // For admin users, always allow access regardless of documents
  if (profile && ['admin', 'superadmin'].includes(profile.role)) {
    return <>{children}</>;
  }

  // Check if user has uploaded BOTH required documents
  const hasRequiredDocuments = profile?.id_front_url && profile?.id_back_url;

  // If documents are required and user hasn't uploaded BOTH documents, show the restriction message
  if (requireDocuments && !hasRequiredDocuments) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center p-4">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <Shield className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
            <CardTitle className="text-xl">
              {t('documents_required', 'مستندات مطلوبة', 'Documents Required')}
            </CardTitle>
            <CardDescription>
              {t('upload_documents_to_access', 'يجب رفع مستندات الهوية للوصول لهذه الصفحة', 'You must upload ID documents to access this page')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <div className="space-y-2">
                  <div className="font-medium">
                    {t('both_documents_required', 'كلا المستندين مطلوبان:', 'Both Documents Required:')}
                  </div>
                  <ul className="list-disc list-inside text-sm space-y-1 ml-4">
                    <li>{t('national_id_front', 'صورة بطاقة الهوية الوجه الأمامي', 'National ID Front Photo')}</li>
                    <li>{t('national_id_back', 'صورة بطاقة الهوية الوجه الخلفي', 'National ID Back Photo')}</li>
                  </ul>
                </div>
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 border rounded-lg bg-muted/20">
                <FileImage className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium mb-1">
                  {t('front_id', 'الهوية الأمامية', 'Front ID')}
                </p>
                <p className={`text-xs ${profile?.id_front_url ? 'text-green-600' : 'text-red-500'}`}>
                  {profile?.id_front_url ? t('uploaded', 'مرفوعة', 'Uploaded') : t('missing', 'مفقودة', 'Missing')}
                </p>
              </div>
              
              <div className="text-center p-3 border rounded-lg bg-muted/20">
                <FileImage className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium mb-1">
                  {t('back_id', 'الهوية الخلفية', 'Back ID')}
                </p>
                <p className={`text-xs ${profile?.id_back_url ? 'text-green-600' : 'text-red-500'}`}>
                  {profile?.id_back_url ? t('uploaded', 'مرفوعة', 'Uploaded') : t('missing', 'مفقودة', 'Missing')}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Button 
                onClick={() => navigate('/account?tab=kyc')} 
                className="w-full"
                size="lg"
              >
                <Upload className="h-4 w-4 mr-2" />
                {t('upload_documents_now', 'رفع المستندات الآن', 'Upload Documents Now')}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => navigate('/')} 
                className="w-full"
              >
                {t('back_to_home', 'العودة للرئيسية', 'Back to Home')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If both documents are uploaded or not required, render children
  return <>{children}</>;
};

export default KYCAccessGuard;
